import {
  STAMMDATEN_NAV,
  getStammdatenGroupForItem,
  getStammdatenNavByKey,
  type NavItem,
  type StammdatenNavGroup,
} from "@/lib/nav";
import { devToolsVisibility } from "@/pages/stammdaten/settings/devToolsVisibility";

/**
 * Einzelne Einstellung oder Aufgabe innerhalb eines Stammdaten-Bereichs.
 * Verlinkt direkt auf den passenden Tab/Abschnitt, damit niemand erst
 * suchen muss, in welchem Bereich eine Einstellung liegt.
 */
export interface StammdatenSearchEntry {
  id: string;
  /** Alltagssprachliche Bezeichnung, z. B. „Anzahlung festlegen“ */
  label: string;
  /** Kurzer Hinweis, was dort eingestellt wird */
  hint: string;
  href: string;
  /** Schlüssel des übergeordneten Navigationseintrags */
  itemKey: string;
  keywords?: string[];
}

export const STAMMDATEN_SEARCH_ENTRIES: StammdatenSearchEntry[] = [
  // Buchungs- & Zahlungsregeln
  {
    id: "deposit",
    label: "Anzahlung festlegen",
    hint: "Prozent, fester Betrag oder erste Nacht mit Gutschrift bei rechtzeitiger Absage",
    href: "/stammdaten/einstellungen?tab=zahlung",
    itemKey: "stammdaten-settings",
    keywords: ["Anzahlungsrechnung", "Vorauszahlung", "Prozent", "Reservierung", "erste Nacht", "Absage"],
  },
  {
    id: "pricing-policy",
    label: "Dynamic Pricing und Frühbucher",
    hint: "Teurer an stark belegten Tagen und günstiger, je früher gebucht wird",
    href: "/stammdaten/einstellungen?tab=zahlung",
    itemKey: "stammdaten-settings",
    keywords: ["Preis", "Auslastung", "Vorjahr", "Frühbucher", "Last Minute", "Aufschlag"],
  },
  {
    id: "payment-terms",
    label: "Zahlungsziel & Zahlungsbedingungen",
    hint: "Nach wie vielen Tagen Rechnungen fällig sind; Text auf dem Rechnungs-PDF",
    href: "/stammdaten/einstellungen?tab=zahlung",
    itemKey: "stammdaten-settings",
    keywords: ["Fälligkeit", "Zahlbar innerhalb", "Zahlungsfrist", "Tage"],
  },
  {
    id: "tourist-payment",
    label: "Touristen: Vorkasse oder Zahlung bei Abreise",
    hint: "Wann für Touristen automatisch ein Rechnungsentwurf geöffnet wird",
    href: "/stammdaten/einstellungen?tab=zahlung",
    itemKey: "stammdaten-settings",
    keywords: ["Vorkasse", "Abreise", "Rechnungsentwurf", "Tourist"],
  },
  {
    id: "checkout",
    label: "Abreise-Uhrzeit & Check-out-Regeln",
    hint: "Ab wann Gäste als überfällig gelten; Check-out bei offenen Rechnungen",
    href: "/stammdaten/einstellungen?tab=buchung",
    itemKey: "stammdaten-settings",
    keywords: ["Checkout", "Abreisezeit", "Überfällig", "11 Uhr", "offene Rechnung", "blockieren"],
  },
  {
    id: "guest-type-defaults",
    label: "Gästetypen: Standard- und Mindestnächte",
    hint: "Voreinstellung für Anreise/Abreise bei neuen Buchungen je Gästetyp",
    href: "/stammdaten/einstellungen?tab=buchung",
    itemKey: "stammdaten-settings",
    keywords: ["Tagesgast", "Dauercamper", "Nächte", "Mindestaufenthalt", "Buchungszeitraum"],
  },
  {
    id: "occupancy",
    label: "Dashboard-Belegung: welche Plätze zählen",
    hint: "Platztypen, die in Belegung und Auslastung auf dem Dashboard einfließen",
    href: "/stammdaten/einstellungen?tab=buchung",
    itemKey: "stammdaten-settings",
    keywords: ["Auslastung", "Belegung", "Kennzahlen", "Tagesreport", "Dauerstellplatz"],
  },
  {
    id: "electricity-billing",
    label: "Strom-Abrechnung (Pauschale oder kWh)",
    hint: "Fester Betrag pro Nacht, nach Verbrauch oder Pauschale mit Freimenge",
    href: "/stammdaten/einstellungen?tab=strom",
    itemKey: "stammdaten-settings",
    keywords: ["kWh", "Zählerstand", "Pauschale", "Verbrauch", "Stromkosten"],
  },
  {
    id: "publish-production",
    label: "Auf Production veröffentlichen",
    hint: "Dev-Stand von dev.app auf app. übertragen",
    href: "/stammdaten/einstellungen?tab=testsystem",
    itemKey: "stammdaten-settings",
    keywords: ["Prod", "Produktion", "deploy", "veröffentlichen", "main", "Admin"],
  },
  {
    id: "clear-test-data",
    label: "Testdaten löschen",
    hint: "Fachdaten des Dev-Mandanten entfernen, Stammdaten behalten",
    href: "/stammdaten/einstellungen?tab=testsystem",
    itemKey: "stammdaten-settings",
    keywords: ["Admin", "Dev", "löschen", "Testdaten"],
  },
  {
    id: "load-demo-data",
    label: "Demodaten laden",
    hint: "Beispiel-Campingbetrieb für Entwicklung und Schulung",
    href: "/stammdaten/einstellungen?tab=testsystem",
    itemKey: "stammdaten-settings",
    keywords: ["Admin", "Demo", "Schulung", "Seed"],
  },
  {
    id: "generate-test-bookings",
    label: "Testdaten für Zeitraum",
    hint: "Zufallsbuchungen nach Tages-Auslastung und Ø-Übernachtungsdauer",
    href: "/stammdaten/einstellungen?tab=testsystem",
    itemKey: "stammdaten-settings",
    keywords: ["Admin", "Testdaten", "Auslastung", "Graph", "Simulation", "Buchungen"],
  },
  {
    id: "clone-production-data",
    label: "Produktionsdaten in die Testumgebung laden",
    hint: "Stand von Mandant default in den Schulungs-Mandanten test kopieren",
    href: "/stammdaten/einstellungen?tab=testsystem",
    itemKey: "stammdaten-settings",
    keywords: ["Admin", "Klon", "Testsystem", "Produktion"],
  },

  // Betriebsdaten
  {
    id: "letterhead",
    label: "Logo, Favicon, Adresse & Briefkopf für Rechnungen",
    hint: "Firmenname, Anschrift, Steuernummer, IBAN, Logo, Favicon und Fußtext",
    href: "/stammdaten/grundeinstellungen",
    itemKey: "stammdaten-grundeinstellungen",
    keywords: ["Logo", "Favicon", "Icon", "IBAN", "BIC", "Bankverbindung", "Steuernummer", "Rechnungskopf", "Fußzeile", "PDF"],
  },
  {
    id: "legal",
    label: "Rechtliche Angaben für die Online-Buchung",
    hint: "Verantwortlicher, Kontakt, Datenschutz-E-Mail, Impressum-Link",
    href: "/stammdaten/grundeinstellungen#rechtliches",
    itemKey: "stammdaten-grundeinstellungen",
    keywords: ["Impressum", "Datenschutz", "DSGVO", "Verantwortlicher", "Aufsichtsbehörde"],
  },

  // Online-Buchung
  {
    id: "online-booking-toggle",
    label: "Online-Buchung ein- oder ausschalten",
    hint: "Buchungswebsite aktivieren, Buchungsmodus und automatische Bestätigung",
    href: "/stammdaten/online-buchung?tab=betrieb",
    itemKey: "stammdaten-online-booking",
    keywords: ["aktivieren", "deaktivieren", "Buchungsmodus", "Bestätigungsmail", "Anfrage"],
  },
  {
    id: "online-booking-seasons",
    label: "Buchungssaisons & Mindestaufenthalt (online)",
    hint: "In welchen Zeiträumen und ab wie vielen Nächten online gebucht werden darf",
    href: "/stammdaten/online-buchung?tab=regeln",
    itemKey: "stammdaten-online-booking",
    keywords: ["Saison", "Hauptsaison", "Nebensaison", "Mindestnächte", "Cutoff", "Same-Day"],
  },
  {
    id: "online-booking-flow",
    label: "Buchungsstrecke & Pflichtfelder für Gäste",
    hint: "Welche Schritte und Formularfelder Gäste online ausfüllen",
    href: "/stammdaten/online-buchung?tab=strecke",
    itemKey: "stammdaten-online-booking",
    keywords: ["Formular", "Pflichtfeld", "Gastdaten", "Begleitpersonen", "Kennzeichen"],
  },
  {
    id: "online-booking-offer",
    label: "Anreisearten, Zusatzartikel & Upselling",
    hint: "Was Gäste online auswählen und dazubuchen können",
    href: "/stammdaten/online-buchung?tab=angebot",
    itemKey: "stammdaten-online-booking",
    keywords: ["Anreiseart", "Wohnmobil", "Zelt", "Extras", "Zusatzleistung", "Upselling", "Darstellung"],
  },

  // Artikel & Preise
  {
    id: "article-price",
    label: "Preis eines Artikels ändern",
    hint: "Neue Preise mit Gültig-ab-Datum, Mehrwertsteuersatz, Einheit",
    href: "/stammdaten/artikel",
    itemKey: "stammdaten-articles",
    keywords: ["Preiserhöhung", "Preis", "gültig ab", "MwSt", "Übernachtung", "Kurtaxe"],
  },
  {
    id: "article-import",
    label: "Artikel per Excel importieren/exportieren",
    hint: "Artikelliste als Excel herunterladen oder hochladen",
    href: "/stammdaten/artikel",
    itemKey: "stammdaten-articles",
    keywords: ["Excel", "Import", "Export", "Liste"],
  },

  // Platzplan-Editor
  {
    id: "site-plan-ai-vectorize",
    label: "Platzplan-Bild mit KI in SVG umwandeln",
    hint: "Planbild hochladen, Flächen und Symbole automatisch erkennen lassen",
    href: "/stammdaten/platzplan-editor",
    itemKey: "stammdaten-site-plan-editor",
    keywords: ["KI", "SVG", "Vektor", "umwandeln", "Lageplan", "Karte"],
  },
  {
    id: "site-plan-symbols",
    label: "Symbole auf dem Platzplan platzieren",
    hint: "Bäume, Stromkästen, Sanitär, Entsorgung und mehr aus der Bibliothek setzen",
    href: "/stammdaten/platzplan-editor",
    itemKey: "stammdaten-site-plan-editor",
    keywords: ["Baum", "Stromkasten", "Symbol", "Bibliothek", "Icon", "WC", "Dusche"],
  },
  {
    id: "site-plan-assign",
    label: "Platzflächen Stellplätzen zuordnen",
    hint: "Flächen im Editor mit Stellplätzen verknüpfen – automatisch nach Nummer oder manuell",
    href: "/stammdaten/platzplan-editor",
    itemKey: "stammdaten-site-plan-editor",
    keywords: ["zuordnen", "Fläche", "Polygon", "Stellplatz", "Nummer"],
  },

  // Stellplätze
  {
    id: "pitch-create",
    label: "Neuen Stellplatz anlegen",
    hint: "Einzeln oder mehrere auf einmal; Kategorie, Größe und Ausstattung",
    href: "/stammdaten/plaetze",
    itemKey: "stammdaten-pitches",
    keywords: ["Stellplatz", "anlegen", "mehrere", "Nummer", "Code"],
  },
  {
    id: "pitch-block",
    label: "Stellplatz sperren oder ausblenden",
    hint: "Sperrzeiträume für Wartung oder Plätze dauerhaft ausblenden",
    href: "/stammdaten/plaetze",
    itemKey: "stammdaten-pitches",
    keywords: ["Sperre", "Sperrzeitraum", "Wartung", "ausblenden", "inaktiv"],
  },
  {
    id: "pitch-features",
    label: "Platz-Eigenschaften verwalten",
    hint: "Merkmale wie Schatten, Seeblick oder WLAN zur Auswahl anlegen",
    href: "/stammdaten/plaetze",
    itemKey: "stammdaten-pitches",
    keywords: ["Eigenschaften", "Merkmale", "Schatten", "WLAN", "Seeblick"],
  },

  // Texte & Vorlagen
  {
    id: "templates-email",
    label: "E-Mail-Texte & Buchungsbestätigung anpassen",
    hint: "Vorlagen mit Platzhaltern für automatische E-Mails und Dokumente",
    href: "/stammdaten/texte",
    itemKey: "stammdaten-templates",
    keywords: ["Buchungsbestätigung", "E-Mail", "Platzhalter", "Anschreiben", "Vorlage"],
  },

  // Schnittstellen
  {
    id: "integration-email",
    label: "E-Mail-Versand einrichten (SMTP / Microsoft)",
    hint: "Absender und Server für Rechnungen und Bestätigungen",
    href: "/stammdaten/schnittstellen?integration=email",
    itemKey: "stammdaten-schnittstellen",
    keywords: ["SMTP", "Postausgang", "Absender", "Microsoft Graph", "Outlook"],
  },
  {
    id: "integration-tse",
    label: "Kasse: TSE / Kassensicherung",
    hint: "Gesetzliche Signierung von Bar- und Kartenzahlungen",
    href: "/stammdaten/schnittstellen?integration=tse",
    itemKey: "stammdaten-schnittstellen",
    keywords: ["TSE", "Kassensicherung", "Fiskal", "Finanzamt", "Kasse"],
  },
  {
    id: "integration-zvt",
    label: "EC-Kartenterminal anbinden",
    hint: "Kartenzahlung an Rezeption, Schnellverkauf und Gäste-Tablet",
    href: "/stammdaten/schnittstellen?integration=zvt",
    itemKey: "stammdaten-schnittstellen",
    keywords: ["EC", "Karte", "Terminal", "ZVT", "Kartenzahlung"],
  },
  {
    id: "integration-device",
    label: "Bondrucker & Geräte an der Kasse",
    hint: "CampingDeviceService für Drucker und Peripherie am Arbeitsplatz",
    href: "/stammdaten/schnittstellen?integration=device-service",
    itemKey: "stammdaten-schnittstellen",
    keywords: ["Bondrucker", "Drucker", "Beleg", "Gerät", "Workstation"],
  },
  {
    id: "integration-fernauslese",
    label: "Stromsteckdosen & Zähler (Fernauslese)",
    hint: "Steckdosen bei Check-in ein- und bei Check-out ausschalten",
    href: "/stammdaten/schnittstellen?integration=fernauslese",
    itemKey: "stammdaten-schnittstellen",
    keywords: ["Strom", "Steckdose", "Zähler", "Fernauslese", "Kanal"],
  },
  {
    id: "integration-barrier",
    label: "Schranke / Zufahrt",
    hint: "Freigabe bei Check-in, Sperre bei Check-out",
    href: "/stammdaten/schnittstellen?integration=barrier",
    itemKey: "stammdaten-schnittstellen",
    keywords: ["Schranke", "Zufahrt", "Tor", "Einfahrt"],
  },
  {
    id: "integration-lpr",
    label: "Kennzeichenerkennung (Kamera)",
    hint: "Kennzeichen-Events empfangen und Schranke öffnen",
    href: "/stammdaten/schnittstellen?integration=lpr",
    itemKey: "stammdaten-schnittstellen",
    keywords: ["Kennzeichen", "Kamera", "Nummernschild", "LPR"],
  },
  {
    id: "integration-tourist-import",
    label: "Daten aus Tourist Pro importieren",
    hint: "Einmaliger Import von Gästen, Buchungen und Rechnungen",
    href: "/stammdaten/schnittstellen?integration=tourist-import",
    itemKey: "stammdaten-schnittstellen",
    keywords: ["Tourist", "Import", "Umstieg", "Altdaten", "CSV"],
  },
  {
    id: "integration-events",
    label: "Veranstaltungen für den Infobildschirm",
    hint: "Eventbrite, Ticketmaster und Eventim automatisch synchronisieren",
    href: "/stammdaten/schnittstellen?integration=events",
    itemKey: "stammdaten-schnittstellen",
    keywords: ["Events", "Veranstaltung", "Eventbrite", "Ticketmaster"],
  },
  {
    id: "integration-channel",
    label: "Buchungsportale & PMS anbinden",
    hint: "Channel-Partner (camping.info, RESAVIO) und PMS-Export",
    href: "/stammdaten/schnittstellen?integration=channel-api",
    itemKey: "stammdaten-schnittstellen",
    keywords: ["Channel", "camping.info", "RESAVIO", "PMS", "Portal", "API"],
  },

  // Konten
  {
    id: "accounts-import",
    label: "Kontenrahmen SKR03/SKR04 laden",
    hint: "Erlöskonten vorbefüllen und Buchhaltungs-Export einstellen",
    href: "/stammdaten/konten",
    itemKey: "stammdaten-accounts",
    keywords: ["SKR03", "SKR04", "Kontenrahmen", "Buchhaltung", "Steuerberater", "DATEV"],
  },

  // Gäste-Tablet / Infobildschirm / Website
  {
    id: "tablet-slideshow",
    label: "Gäste-Tablet: Bilder & Begrüßungstexte",
    hint: "Diashow im Ruhezustand und Textfelder am Check-in-Terminal",
    href: "/stammdaten/gaeste-tablet",
    itemKey: "stammdaten-guest-tablet",
    keywords: ["Diashow", "Bilder", "Begrüßung", "Ruhebildschirm", "Arbeitsplatz"],
  },
  {
    id: "info-screen-pages",
    label: "Infobildschirm: Seiten & Veranstaltungen",
    hint: "Inhalte, Reihenfolge und Geräte für Bildschirme im Gelände",
    href: "/stammdaten/infobildschirm",
    itemKey: "stammdaten-info-screen",
    keywords: ["Bildschirm", "Seite", "Veranstaltung", "Wetter", "Anzeige"],
  },
  {
    id: "website-content",
    label: "Fotos und Kacheln der Website",
    hint: "Eigene Kacheln anlegen, Fotos groß zeigen und Texte ändern",
    href: "/stammdaten/website",
    itemKey: "stammdaten-website",
    keywords: ["Website", "Text", "Bild", "Galerie", "Startseite", "Inhalt", "Kachel", "Foto"],
  },
  {
    id: "website-news",
    label: "Meldung auf der Website veröffentlichen",
    hint: "Aktuelle Hinweise, z. B. Öffnungszeiten oder Veranstaltungen",
    href: "/stammdaten/website-news",
    itemKey: "stammdaten-website-news",
    keywords: ["News", "Meldung", "Hinweis", "Aktuelles", "Öffnungszeiten"],
  },
];

/** Häufige Aufgaben für den Schnellzugriff auf der Übersichtsseite */
export const STAMMDATEN_QUICK_TASK_IDS: string[] = [
  "article-price",
  "pitch-create",
  "deposit",
  "checkout",
  "letterhead",
  "templates-email",
  "online-booking-toggle",
  "website-news",
];

export function getStammdatenSearchEntry(id: string): StammdatenSearchEntry | undefined {
  return STAMMDATEN_SEARCH_ENTRIES.find((entry) => entry.id === id);
}

export interface StammdatenSearchResult {
  kind: "area" | "setting";
  id: string;
  label: string;
  hint: string;
  href: string;
  item: NavItem;
  group?: StammdatenNavGroup;
  score: number;
}

/**
 * Umlaute auf Grundvokale abbilden, damit „Stellplatz“ auch „Stellplätze“
 * findet (und umgekehrt „Plaetze“ weiterhin passt).
 */
function normalize(value: string): string {
  return value
    .toLocaleLowerCase("de")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/ß/g, "ss")
    .replace(/ae/g, "a")
    .replace(/oe/g, "o")
    .replace(/ue/g, "u")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function tokenize(value: string): string[] {
  return normalize(value).split(" ").filter(Boolean);
}

/**
 * Bewertet, wie gut ein Eintrag zu den Suchbegriffen passt.
 * Alle Begriffe müssen irgendwo vorkommen; Treffer im Label zählen stärker.
 */
function scoreMatch(terms: string[], label: string, secondary: string[]): number {
  const labelNorm = normalize(label);
  const secondaryNorm = secondary.map(normalize).join(" ");
  let score = 0;
  for (const term of terms) {
    if (labelNorm.includes(term)) {
      score += labelNorm.startsWith(term) ? 6 : 4;
    } else if (secondaryNorm.includes(term)) {
      score += 2;
    } else {
      return 0;
    }
  }
  return score;
}

const ADMIN_TOOL_SEARCH_IDS = new Set([
  "publish-production",
  "clear-test-data",
  "load-demo-data",
  "generate-test-bookings",
  "clone-production-data",
]);

function adminToolSearchVisible(id: string): boolean {
  if (!ADMIN_TOOL_SEARCH_IDS.has(id)) return true;
  const visibility = devToolsVisibility();
  if (id === "publish-production") return visibility.showPublishProduction;
  if (id === "clear-test-data") return visibility.showClearTestData;
  if (id === "load-demo-data" || id === "generate-test-bookings") {
    return visibility.showDemoSeedTools;
  }
  if (id === "clone-production-data") return visibility.showProductionClone;
  return false;
}

export function searchStammdaten(query: string, limit = 10): StammdatenSearchResult[] {
  const terms = tokenize(query);
  if (terms.length === 0) return [];

  const results: StammdatenSearchResult[] = [];

  for (const item of STAMMDATEN_NAV) {
    const score = scoreMatch(terms, item.label, [
      item.description ?? "",
      ...(item.keywords ?? []),
    ]);
    if (score > 0) {
      results.push({
        kind: "area",
        id: item.key,
        label: item.label,
        hint: item.description ?? "",
        href: item.href,
        item,
        group: getStammdatenGroupForItem(item.key),
        score,
      });
    }
  }

  // Konkrete Einstellungen leicht bevorzugen: Sie führen direkt zum Ziel,
  // während der Bereich nur die Seite öffnet.
  for (const entry of STAMMDATEN_SEARCH_ENTRIES) {
    if (!adminToolSearchVisible(entry.id)) continue;
    const item = getStammdatenNavByKey(entry.itemKey);
    if (!item) continue;
    const score = scoreMatch(terms, entry.label, [entry.hint, ...(entry.keywords ?? [])]);
    if (score > 0) {
      results.push({
        kind: "setting",
        id: entry.id,
        label: entry.label,
        hint: entry.hint,
        href: entry.href,
        item,
        group: getStammdatenGroupForItem(item.key),
        score: score + 1,
      });
    }
  }

  return results.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label, "de")).slice(0, limit);
}
